import React, { createContext, Dispatch, FC, useContext, useEffect, useReducer } from 'react';
import { env } from '../../../config/env';

declare let Help4: any;

// This interface is the one exposed by the useWebAssistant hook
interface WebAssistant {
  isBusy: boolean;
  isAvailable: boolean;
  isMinimized: boolean;
  isActive: boolean;
  isCarouselVisible: boolean;
  isLoading: boolean;
  indentation: {
    shouldIndent: boolean;
    widthInPx: number;
  };
  helpMode?: any;
  internalApi?: any;
  Help4?: any;
}

type WebAssistantReducerAction = { propertyName: keyof WebAssistant; newValue: any };

const webAssistantReducer = (
  state: WebAssistant,
  action: WebAssistantReducerAction
): WebAssistant => {
  const newState = { ...state };
  newState[action.propertyName] = action.newValue;
  return newState;
};

// Global variables scoped to the file to hold the global state3
const initialWebAssistant = {
  isBusy: false,
  isAvailable: false,
  isMinimized: false,
  isActive: false,
  isCarouselVisible: false,
  isLoading: false,
  indentation: {
    shouldIndent: false,
    widthInPx: 0
  }
} as WebAssistant;

const webAssistantContext = createContext({ webAssistant: initialWebAssistant });

const loadWebAssistantScript = async () => {
  // Try to load for 30 seconds
  const maxTries = 30;
  for (let index = 0; index < maxTries; index += 1) {
    // Help4 is a global variable created when the web assistant script is loaded
    if (typeof Help4 !== 'undefined') {
      return;
    }
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => setTimeout(resolve, 1000));
  }
  throw Error;
};

const setupWebAssistantAPI = (updateProperty: Dispatch<WebAssistantReducerAction>): any => {
  const product = env?.REACT_APP_WEB_ASSISTANT_PRODUCT;
  const version = env?.REACT_APP_WEB_ASSISTANT_VERSION;
  const appName = env?.REACT_APP_WEB_ASSISTANT_APP_NAME;
  const stateUACP = env?.REACT_APP_WEB_ASSISTANT_STATE_UACP;

  return Help4.init({
    // ---- General Settings ----
    type: 'default', // alternatives: "ui5", "fiori"
    serviceLayerVersion: 'UACP', // alternative "UACP"
    // This config can be changed via query-parameter, for example ?help-stateUACP=DRAFT
    // The suggested mapping is the following:
    // production -> PRODUCTION
    // development -> DRAFT
    // others -> TEST
    stateUACP,

    // Where to load CSS and other code files from: DON'T CHANGE THIS
    resourceUrl: 'https://webassistant.enable-now.cloud.sap/web_assistant/framework/',
    // Where to download help content from: DON'T CHANGE THIS
    // If necessary, this URL can be changed via query-parameter, for example ?help-dataUrlUACP=https://help.sap.com/webassistant/sample
    dataUrlUACP: 'https://help.sap.com/webassistant/',
    mediaUrlUACP: 'https://help.sap.com/doc/',
    isUACP2: true,

    // ---- Content Settings ----
    // the ID, version, system of your product - use the same information in content metadata
    // suggestion: parametrize these fields with your other env variables
    product,
    version,

    // name of the initial app/screen of your application
    // can also be set dynamically via Help4.API.setScreen(<appname>) or setAppName
    appName,

    // ---- User Settings ----
    // Recommended: the language settings for the current user - take it from your UI settings
    language: 'en-US', // will always fall back to en-US if your language is not supported by Web Assistant
    rtl: 'false', // enable rtl, set based on selected language
    openImmediately: 'false', // open Web Assistant help panel directly on start
    showCloseButton: 'true', // Enables the 'X' button to close drawer

    // usually off
    mobile: 'false', // mobile device support (tablets only)

    // optional: should Web Assistant start in author mode?
    // set to false for productive use, author mode can be entered by adding ?help-editor=true to the URL of the page
    editor: false,

    // optional: enable support for non single-page applications
    multipage: true,

    // Only retrieve the content (and attempts to log the user) when the help button is clicked
    readCatalogue: false,

    // --- Optional: content server and workarea id for the learning tile ---
    // learningAppBackendUrl: 'https://sen-pm.enable-now.cloud.sap/',
    // learningAppWorkspace: 'sampleapp',

    // optional: if you have a generic "parameters" parameter in your config UI, you can pass the value here
    // parameters: 'version=1802 500;product=SAP_S4HANA_CLOUD;theme=light;learningAppBackendUrl=https://education.hana.ondemand.com/education/;learningAppWorkspace=s4',

    // optional: color theme
    // note that Web Assistant has a fixed set of available themes, it will not use the UI5 theme
    // eslint-disable-next-line no-undef
    theme: 'quartz', // 'default' , 'light', 'sfsf', hcb' (high-contrast-black), hcw (high-contrast-white), quartz, quartzlight

    onHelpBusy(isBusy: any) {
      // mark help button to indicate that help is loading
      updateProperty({ propertyName: 'isBusy', newValue: isBusy });
    },

    // called by Web Assistant after setAppId / setScreen / setSubScreen to indicate if help was found for the screen
    // typically used to hide or grey out help button if no help is available
    onHelpAvailable(isAvailable: any) {
      updateProperty({ propertyName: 'isAvailable', newValue: isAvailable });
    },

    // called by Web Assistant when help is minimized via ">>" button or API call
    onHelpMinimized(isMinimized: any) {
      // if help is minimized, help button is highlighted to indicate that you need to click it to unminimize
      updateProperty({ propertyName: 'isMinimized', newValue: isMinimized });
    },

    // called by Web Assistant after help has been switched on/off via the toggle function
    onHelpActive(isActive: any) {
      // mark help toggle button that help is currently switched on
      updateProperty({ propertyName: 'isActive', newValue: isActive });
    },

    // called by Web Assistant after the help mode has changed between HELP and TOUR
    onHelpMode(helpMode: string) {
      updateProperty({ propertyName: 'helpMode', newValue: helpMode });
    },

    // called by Web Assistant when the visibility of the carousel has changed
    onHelpCarousel(isCarouselVisible: any) {
      updateProperty({ propertyName: 'isCarouselVisible', newValue: isCarouselVisible });
    },

    // called by Web Assistant when the carousel slides out vertically
    onHelpRequireIndent(shouldIndent: any, widthInPx: number) {
      if (shouldIndent) {
        document.documentElement.style.marginRight = `${widthInPx}px`;
      } else {
        document.documentElement.style.marginRight = '0';
      }
      updateProperty({ propertyName: 'indentation', newValue: { shouldIndent, widthInPx } });
    },

    // allowing to modify which screen elements can be identified and how
    // usually you don't need to touch these
    selector: {
      // use DataAttrSelector (checking for data-help-id)
      // other options: 'IdSelectorUI5', 'ClassSelector', 'DataCatalogSelector', 'BindingInfoUI5', 'DomSelectorUI5'
      blacklist: [],
      rules: ['DataAttrSelector', 'DataUI5Stable'],
      selectors: {
        // This custom selector allows WebAssistant to select components from the shadow dom that otherwise could not be select
        // TabContainer is one example of usage, where the data-ui5-stable is the only way to select the tab buttons
        DataUI5Stable: {
          getElement: Help4.selector.methods.Utils.getElement,
          getSelector(elem: any): null | { value: string; safe: boolean } {
            const attribute = elem.getAttribute('data-ui5-stable');
            if (attribute && attribute.length > 0) {
              return { value: `${elem.nodeName}[data-ui5-stable="${attribute}"]`, safe: true };
            }
            return null;
          }
        }
      }
    }
  }); // end of init function call
};

const loadWebAssistantForTheFirstTime = (
  updateProperty: Dispatch<WebAssistantReducerAction>
): void => {
  updateProperty({ propertyName: 'isLoading', newValue: true });
  loadWebAssistantScript().then(() => {
    const api = setupWebAssistantAPI(updateProperty);
    updateProperty({ propertyName: 'internalApi', newValue: api });
    updateProperty({ propertyName: 'isLoading', newValue: false });
  });
};

interface WebAssistantProviderProps {
  children: React.ReactNode;
}

const { Provider } = webAssistantContext;
const WebAssistantProvider: FC<WebAssistantProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(webAssistantReducer, initialWebAssistant);
  useEffect(() => {
    loadWebAssistantForTheFirstTime(dispatch);
  }, []);
  return <Provider value={{ webAssistant: state }}>{children}</Provider>;
};

const useWebAssistant = () => useContext(webAssistantContext);

export { WebAssistantProvider, useWebAssistant };
